import React, { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { TagBadge } from '../ui/tag-badge';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../ui/context-menu';
import { User, Wifi, WifiOff, ArrowRight, Settings2, FileText, MessageSquare, Check, Pin } from 'lucide-react';
import { KanbanTicket, KanbanColuna } from '../../services/kanbanService';
import { getFileUrl, getInitials } from '../../utils/dashboardHelpers';

interface KanbanCardProps {
  ticket: KanbanTicket;
  colunaId: string;
  colunas: KanbanColuna[];
  onMoverTicket: (ticketId: string, colunaId: string | null, status?: string) => void;
  onAbrirCampos: (ticket: KanbanTicket) => void;
  onAbrirNota: (ticket: KanbanTicket) => void;
  onAbrirAtendimento: (ticket: KanbanTicket) => void;
  onClick: (ticket: KanbanTicket) => void;
  onConfirmarFechar?: (ticketId: string, colunaId: string) => void;
  canMoveToPendentes?: (fromColunaId: string) => boolean;
  onFixar?: (ticketId: string) => void;
}

export const KanbanCard = memo(({
  ticket,
  colunaId,
  colunas,
  onMoverTicket,
  onAbrirCampos,
  onAbrirNota,
  onAbrirAtendimento,
  onClick,
  onConfirmarFechar,
  canMoveToPendentes,
  onFixar,
}: KanbanCardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.id,
    data: { ticket }
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 9999 : 'auto' as any,
  };

  const isOnline = ticket.instancia?.statusConexao === 'open';

  const FECHADOS_TITLES = ['Fechados'];

  const handleMover = (coluna: KanbanColuna) => {
    if (FECHADOS_TITLES.includes(coluna.titulo)) {
      onConfirmarFechar?.(ticket.id, coluna.id);
      return;
    }
    const statusMap: Record<string, string> = {
      'Pendentes': 'pendente',
      'Abertos': 'aberto',
      'Fechados': 'fechado'
    };
    const novoStatus = statusMap[coluna.titulo];
    onMoverTicket(ticket.id, coluna.id, novoStatus || undefined);
  };

  const isMoveDisabled = (coluna: KanbanColuna): boolean => {
    if (coluna.id === colunaId) return true;
    if (coluna.titulo === 'Pendentes' && canMoveToPendentes && !canMoveToPendentes(colunaId)) return true;
    return false;
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={setNodeRef}
          style={style}
          {...listeners}
          {...attributes}
          className={`relative bg-white dark:bg-slate-700 rounded-lg p-3 border border-gray-100 dark:border-slate-600 
            shadow-sm hover:shadow-md transition-all duration-200 cursor-grab active:cursor-grabbing
            hover:border-gray-300 dark:hover:border-slate-500 group select-none
            ${ticket.ultimaMensagem?.remetenteTipo !== 'atendente'
              ? 'before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1.5 before:bg-orange-400 before:rounded-l-lg'
              : ''
            }
            ${isDragging ? 'ring-2 ring-purple-400 shadow-lg' : ''}`}
          onClick={() => onClick(ticket)}
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={getFileUrl(ticket.avatar ?? undefined)} alt={ticket.nome} />
                <AvatarFallback className="bg-gray-200 dark:bg-slate-600 text-xs text-gray-600 dark:text-gray-300">
                  {getInitials(ticket.nome)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {ticket.nome}
              </span>
              {ticket.fixada && (
                <Pin size={12} className="text-yellow-500 flex-shrink-0" />
              )}
            </div>
            <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap ml-1">
              {formatDistanceToNow(new Date(ticket.dataAtualizacao), { addSuffix: false, locale: ptBR })}
            </span>
          </div>

          {ticket.ultimaMensagem?.conteudo && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-2 pl-10">
              {ticket.ultimaMensagem.conteudo.length > 40
                ? ticket.ultimaMensagem.conteudo.slice(0, 40) + '...'
                : ticket.ultimaMensagem.conteudo
              }
            </p>
          )}

          {ticket.ticketTags?.length > 0 && (
            <div className="flex items-center gap-1 mb-2 pl-10 flex-wrap">
              {ticket.ticketTags.slice(0, 2).map(({ tag }) => (
                <TagBadge key={tag.id} titulo={tag.titulo} cor={tag.cor} prioridade={tag.prioridade} />
              ))}
              {ticket.ticketTags.length > 2 && (
                <span className="text-[10px] text-gray-400">+{ticket.ticketTags.length - 2}</span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-[11px] text-gray-400 dark:text-gray-500 pl-10">
            {ticket.instancia && (
              <span className="flex items-center gap-1">
                {isOnline ? <Wifi size={10} className="text-green-500" /> : <WifiOff size={10} className="text-red-400" />}
                <span className="truncate max-w-[80px]">{ticket.instancia.nome}</span>
              </span>
            )}
            {ticket.atendente && (
              <span className="flex items-center gap-1">
                <User size={10} />
                <span className="truncate max-w-[80px]">{ticket.atendente.nome}</span>
              </span>
            )}
          </div>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent className="w-60 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 shadow-lg rounded-lg p-1">
        <ContextMenuLabel className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2 py-1.5 flex items-center gap-1.5">
          <ArrowRight size={12} />
          Mover para
        </ContextMenuLabel>
        {colunas.map(coluna => {
          const disabled = isMoveDisabled(coluna);
          const isFechados = FECHADOS_TITLES.includes(coluna.titulo);
          return (
            <ContextMenuItem
              key={coluna.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm ${
                disabled
                  ? 'opacity-40 cursor-not-allowed'
                  : 'cursor-pointer focus:bg-gray-100 dark:focus:bg-slate-700'
              }`}
              disabled={disabled}
              onClick={() => !disabled && handleMover(coluna)}
              title={disabled && coluna.titulo === 'Pendentes' ? 'Não é possível voltar de Abertos para Pendentes' : undefined}
            >
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: coluna.cor || '#6B7280' }}
              />
              <span className="flex-1">{coluna.titulo}</span>
              {coluna.id === colunaId && (
                <Check size={14} className="text-green-500 flex-shrink-0" />
              )}
            </ContextMenuItem>
          );
        })}
        <ContextMenuSeparator className="my-1" />
        <ContextMenuItem
          className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer focus:bg-gray-100 dark:focus:bg-slate-700 text-sm"
          onClick={() => onFixar?.(ticket.id)}
        >
          <Pin size={14} className={ticket.fixada ? 'text-yellow-500' : 'text-gray-500'} />
          <span>{ticket.fixada ? 'Desafixar do topo' : 'Fixar no topo'}</span>
        </ContextMenuItem>
        <ContextMenuItem
          className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer focus:bg-gray-100 dark:focus:bg-slate-700 text-sm"
          onClick={() => onAbrirCampos(ticket)}
        >
          <Settings2 size={14} className="text-gray-500" />
          <span>Preencher Campo</span>
        </ContextMenuItem>
        <ContextMenuItem
          className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer focus:bg-gray-100 dark:focus:bg-slate-700 text-sm"
          onClick={() => onAbrirNota(ticket)}
        >
          <FileText size={14} className="text-gray-500" />
          <span>Adicionar Nota Interna</span>
        </ContextMenuItem>
        <ContextMenuSeparator className="my-1" />
        <ContextMenuItem
          className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer focus:bg-gray-100 dark:focus:bg-slate-700 text-sm"
          onClick={() => onAbrirAtendimento(ticket)}
        >
          <MessageSquare size={14} className="text-gray-500" />
          <span>Abrir Atendimento</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
});

KanbanCard.displayName = 'KanbanCard';
